import React, { useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { X, Home, Map as MapIcon, Tag, Heart, Ticket, MessageSquare, User, Languages, LogOut, ChevronRight, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import useUnreadNotifications from "@/hooks/useUnreadNotifications";

const ITEMS = [
  { to: "/app", icon: Home, key: "tab.home", end: true, testid: "menu-home" },
  { to: "/app/notifications", icon: Bell, key: "menu.notifications", testid: "menu-notifications", showUnread: true },
  { to: "/app/map", icon: MapIcon, key: "tab.map", testid: "menu-map" },
  { to: "/app/offers", icon: Tag, key: "menu.offers", testid: "menu-offers" },
  { to: "/app/favorites", icon: Heart, key: "menu.favorites", testid: "menu-favorites" },
  { to: "/app/coupons", icon: Ticket, key: "menu.myCoupons", testid: "menu-coupons" },
  { to: "/app/messages", icon: MessageSquare, key: "tab.messages", testid: "menu-messages" },
  { to: "/app/profile", icon: User, key: "tab.profile", testid: "menu-profile" },
];

export default function MenuDrawer({ open, onClose }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { count: unread } = useUnreadNotifications();

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  const roleKey =
    user?.role === "shop_owner"
      ? "profile.owner"
      : user?.role === "admin"
      ? "profile.admin"
      : "profile.customer";

  // Close only when pathname genuinely changes (not on open-state toggle)
  const prevPath = React.useRef(pathname);
  useEffect(() => {
    if (prevPath.current !== pathname) {
      prevPath.current = pathname;
      onClose?.();
    }
  }, [pathname, onClose]);

  // Lock scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleSignOut = () => {
    onClose?.();
    logout();
    nav("/", { replace: true });
  };

  const toggleLang = () => setLang(lang === "en" ? "ml" : "en");

  return (
    <>
      {/* Scrim */}
      <div
        data-testid="menu-scrim"
        onClick={onClose}
        aria-hidden={!open}
        className={`fixed inset-0 z-[70] bg-foreground/30 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      {/* Drawer */}
      <aside
        data-testid="menu-drawer"
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        className={`fixed z-[80] top-0 bottom-0 right-0 w-[86%] max-w-[380px] bg-background shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-6 pb-5">
          <span className="grid place-items-center w-14 h-14 rounded-full bg-primary text-primary-foreground text-2xl font-extrabold shrink-0">
            {initial}
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-display font-extrabold text-2xl leading-tight truncate">
              {user?.name || "—"}
            </p>
            <p className="text-muted-foreground text-sm capitalize truncate">{t(roleKey).toLowerCase()}</p>
          </div>
          <button
            data-testid="menu-close"
            onClick={onClose}
            aria-label="Close menu"
            className="grid place-items-center w-10 h-10 rounded-full hover:bg-secondary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mx-5 h-px bg-border" />

        {/* Nav list */}
        <nav className="flex-1 overflow-y-auto px-3 py-3">
          <ul className="space-y-1.5">
            {ITEMS.map(({ to, icon: Icon, key, end, testid, showUnread }) => {
              const active = end ? pathname === to : pathname.startsWith(to);
              return (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    data-testid={testid}
                    className={`flex items-center gap-4 rounded-2xl px-3 py-3 transition-colors ${
                      active ? "bg-[#EDF3EA]" : "hover:bg-secondary"
                    }`}
                  >
                    <span
                      className={`relative grid place-items-center w-11 h-11 rounded-full shrink-0 ${
                        active
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-primary"
                      }`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={active ? 2.4 : 2} />
                      {showUnread && unread > 0 && (
                        <span
                          data-testid="menu-notif-badge"
                          className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-extrabold grid place-items-center border-2 border-background"
                        >
                          {unread > 99 ? "99+" : unread}
                        </span>
                      )}
                    </span>
                    <span className={`font-semibold text-lg ${active ? "text-primary" : "text-foreground"}`}>
                      {t(key)}
                    </span>
                    {active && <span className="ml-auto w-2.5 h-2.5 rounded-full bg-primary" />}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="mx-5 h-px bg-border" />

        {/* Language row */}
        <button
          data-testid="menu-lang"
          onClick={toggleLang}
          className="flex items-center gap-4 px-6 py-4 hover:bg-secondary transition-colors"
        >
          <span className="grid place-items-center w-11 h-11 rounded-full bg-secondary text-primary shrink-0">
            <Languages className="w-5 h-5" />
          </span>
          <span className="flex-1 text-left">
            <p className="font-semibold text-lg">{lang === "en" ? t("profile.english") : t("profile.malayalam")}</p>
            <p className="text-sm text-muted-foreground">
              {lang === "en" ? t("profile.english") : t("profile.malayalam")}
            </p>
          </span>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Sign out */}
        <button
          data-testid="menu-signout"
          onClick={handleSignOut}
          className="flex items-center gap-4 px-6 py-4 hover:bg-primary/5 transition-colors"
        >
          <span className="grid place-items-center w-11 h-11 rounded-full bg-primary/10 text-primary shrink-0">
            <LogOut className="w-5 h-5" />
          </span>
          <span className="font-semibold text-lg text-primary">{t("profile.signOut")}</span>
        </button>
      </aside>
    </>
  );
}
